// src/App.tsx
/**
 * Premium 3D Portfolio App
 *
 * Cinematic visual quality with smooth, reactive navigation.
 * Uses centralized scene system for consistency.
 */

import { Suspense, useState, useEffect, useCallback, useRef } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { Preload } from '@react-three/drei'
import { Leva } from 'leva'

// Scene system
import {
  StageEnvironment,
  CameraSystem,
  palette,
  spacing,
  radii,
  world,
  OfficeRoom,
} from './scene'
import { StudioLighting } from './scene/LightingRig'
// Stage components
import { IntroStage } from './scene/stages'

// Ambient Particles (drei)
import AmbientParticles from './scene/AmbientParticles'

// Error Boundary
import { ErrorBoundary } from './components/ErrorBoundary'

// Avatar & Voice
import Avatar3D from './components/Avatar3D'
import { VoiceProvider, useVoiceState } from './hooks/useVoice'
import { useTranscriptStore } from './hooks/useTranscriptStore'
import { useShellPalette } from './hooks/useShellPalette'

// Configure unified Omote logger (from /logging subpath to avoid pulling ONNX Runtime)
import { configureLogging } from '@omote/core/logging'
import { configureModelUrls, configureTelemetry } from '@omote/core'

configureLogging({
  level: 'info',
  format: 'pretty',
})

// Enable telemetry when running locally — sends to local OTLP collector → Grafana
if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
  configureTelemetry({
    enabled: true,
    serviceName: 'sepehr-portfolio',
    exporter: 'otlp',
    exporterConfig: { endpoint: 'http://localhost:4318' },
    metricsIntervalMs: 10_000,
  })
}

// Point models at self-hosted CDN for fast, reliable delivery.
// If this call is removed, all models fall back to HuggingFace CDN automatically.
configureModelUrls({
  lam: '/models/lam_ios.onnx',
  senseVoice: '/models/sensevoice/model.int8.onnx',
  sileroVad: '/models/silero-vad.onnx',
})

// ============================================================================
// MAIN SCENE
// ============================================================================

function Scene() {
  return (
    <>
      {/* ================================================================ */}
      {/* CAMERA SYSTEM (spring-damped transitions + input handling)       */}
      {/* ================================================================ */}
      <CameraSystem />

      {/* ================================================================ */}
      {/* STUDIO LIGHTING                                                   */}
      {/* Wrapped in Suspense: loads HDRI environment map                  */}
      {/* ================================================================ */}
      <Suspense fallback={null}>
        <StudioLighting intensity={1} />
      </Suspense>

      {/* ================================================================ */}
      {/* STAGE ENVIRONMENT (ground + shadows + fog)                       */}
      {/* ================================================================ */}
      <StageEnvironment
        groundY={world.ground.y}
        shadowType="contact"
        showFog={true}
      />

      {/* ================================================================ */}
      {/* OFFICE ROOM (Low Poly Room)                                     */}
      {/* Wrapped in Suspense: loads GLTF model                           */}
      {/* ================================================================ */}
      <Suspense fallback={null}>
        <OfficeRoom
          position={[0, world.ground.y, -0.2]}
          rotation={[0, -.8, 0]}
          scale={0.7}
        />
      </Suspense>

      {/* ================================================================ */}
      {/* AMBIENT PARTICLES (sparkles + stars)                             */}
      {/* ================================================================ */}
      <AmbientParticles sparkles={true} stars={true} />

      {/* ================================================================ */}
      {/* AVATAR                                                           */}
      {/* Wrapped in Suspense: loads GLB + FBX                             */}
      {/* ================================================================ */}
      <Suspense fallback={null}>
        <Avatar3D scale={0.95} />
      </Suspense>

      {/* ================================================================ */}
      {/* INTRO PANEL (the only persistent UI)                             */}
      {/* ================================================================ */}
      <IntroStage />

    </>
  )
}

// ============================================================================
// TRANSCRIPT OVERLAY (HTML — outside R3F Canvas to avoid GPU contention)
// ============================================================================

function TranscriptOverlay() {
  const transcript = useTranscriptStore((s) => s.transcript)
  const interimTranscript = useTranscriptStore((s) => s.interimTranscript)
  const transcriptStatus = useTranscriptStore((s) => s.transcriptStatus)
  const shell = useShellPalette()

  // Auto-hide: fade out 5s after last content change
  const [visible, setVisible] = useState(false)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const hasContent = !!(transcript.user || transcript.assistant || (transcriptStatus === 'interim' && interimTranscript))

  useEffect(() => {
    if (hasContent) {
      setVisible(true)
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = setTimeout(() => setVisible(false), 12000)
    }
    return () => clearTimeout(hideTimerRef.current)
  }, [transcript.user, transcript.assistant, interimTranscript, hasContent])

  // Show overlay if there's any text to display (including interim)
  const hasInterim = transcriptStatus === 'interim' && interimTranscript
  if (!hasContent && !visible) {
    return null
  }

  // Determine user text to display: interim (live) vs final (sent)
  const showInterim = hasInterim && !transcript.user
  const userDisplayText = showInterim
    ? interimTranscript + '...'
    : transcript.user

  // Visual distinction: interim = dimmer, final = brief green border accent
  const userTextColor = showInterim ? shell.textSecondary : shell.textPrimary
  const borderColor = transcriptStatus === 'final' ? shell.brandSuccess : shell.glassBorder

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: window.innerWidth <= 768 ? 12 : 40,
        paddingLeft: 16,
        paddingRight: 16,
        pointerEvents: 'none',
        zIndex: 20,
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.6s ease',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing.sm,
          padding: spacing.md,
          background: shell.glassBackground,
          borderRadius: radii.lg,
          width: '100%',
          maxWidth: 500,
          border: `1px solid ${borderColor}`,
          backdropFilter: 'blur(12px)',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        {userDisplayText && (
          <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'flex-start' }}>
            <span
              style={{
                fontSize: 12,
                color: shell.brandSuccess,
                fontWeight: 'bold',
                flexShrink: 0,
              }}
            >
              You:
            </span>
            <span style={{ fontSize: 12, color: userTextColor, flexGrow: 1 }}>
              {userDisplayText}
            </span>
          </div>
        )}
        {transcript.assistant && (
          <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'flex-start' }}>
            <span
              style={{
                fontSize: 12,
                color: shell.brandPrimary,
                fontWeight: 'bold',
                flexShrink: 0,
              }}
            >
              Sepehr:
            </span>
            <span style={{ fontSize: 12, color: shell.textPrimary, flexGrow: 1 }}>
              {transcript.assistant}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// LOADING SCREEN (shown while R3F assets load)
// ============================================================================

function LoadingScreen({ ready }: { ready: boolean }) {
  const [visible, setVisible] = useState(true)
  const shell = useShellPalette()

  useEffect(() => {
    if (ready) {
      const timer = setTimeout(() => setVisible(false), 600)
      return () => clearTimeout(timer)
    }
  }, [ready])

  if (!visible) return null

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: shell.pageBackground,
        transition: 'opacity 0.5s ease-out',
        opacity: ready ? 0 : 1,
        pointerEvents: ready ? 'none' : 'auto',
      }}
    >
      <h1
        style={{
          fontSize: '1.5rem',
          fontWeight: 600,
          color: shell.textPrimary,
          marginBottom: '1.5rem',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          letterSpacing: '-0.02em',
        }}
      >
        Sepehr Nourbakhsh
      </h1>
      <div
        style={{
          width: '200px',
          height: '2px',
          background: shell.progressTrack,
          borderRadius: '1px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: '40%',
            height: '100%',
            background: shell.brandPrimary,
            borderRadius: '1px',
            animation: 'loading-slide 1.2s ease-in-out infinite',
          }}
        />
      </div>
      <style>{`
        @keyframes loading-slide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
      `}</style>
    </div>
  )
}

// ============================================================================
// ASSET READY TRACKER (signals when R3F assets finish loading)
// ============================================================================

function AssetReadySignal({ onReady }: { onReady: () => void }) {
  const frameCount = useRef(0)

  // Wait for the renderer to warm up (shader compilation, texture uploads)
  // before signaling ready. This prevents the loading screen from fading
  // out while the GPU is still compiling shaders on first render.
  useFrame(() => {
    frameCount.current++
    if (frameCount.current === 30) {
      onReady()
    }
  })

  return null
}

// ============================================================================
// SCENE FAILED FALLBACK (shown when WebGL/Canvas crashes)
// ============================================================================

function SceneFailedFallback() {
  const shell = useShellPalette()
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: shell.pageBackground,
        color: shell.textSecondary,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        padding: '2rem',
        textAlign: 'center',
      }}
    >
      <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '0.5rem', color: shell.textPrimary }}>
        Sepehr Nourbakhsh
      </h1>
      <p style={{ fontSize: '1rem', color: shell.textSecondary, marginBottom: '2rem' }}>
        Software Engineer &amp; AI Enthusiast
      </p>
      <p style={{ fontSize: '0.875rem', color: shell.textTertiary, marginBottom: '2rem', maxWidth: '400px' }}>
        3D scene could not load. Here is my portfolio:
      </p>
      <div style={{ display: 'flex', gap: '1.5rem' }}>
        <a
          href="https://github.com/sepehrnour"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: shell.link, textDecoration: 'none', fontSize: '0.95rem' }}
        >
          GitHub
        </a>
        <a
          href="https://linkedin.com/in/sepehr-nour"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: shell.link, textDecoration: 'none', fontSize: '0.95rem' }}
        >
          LinkedIn
        </a>
        <a
          href="mailto:sepehrnour@gmail.com"
          style={{ color: shell.link, textDecoration: 'none', fontSize: '0.95rem' }}
        >
          Email
        </a>
      </div>
    </div>
  )
}

// ============================================================================
// APP CONTENT (inside VoiceProvider to access loading state)
// ============================================================================

function AppContent() {
  const [r3fReady, setR3fReady] = useState(false)
  const [onnxStarted, setOnnxStarted] = useState(false)
  const [levaVisible, setLevaVisible] = useState(false)
  const { loadModels } = useVoiceState()
  const shell = useShellPalette()
  const handleR3fReady = useCallback(() => setR3fReady(true), [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'h' && e.key !== 'H') return
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
      setLevaVisible((v) => !v)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Delay ONNX model loading until after R3F sweep-in animation completes.
  // Uses requestIdleCallback so loading starts when the browser is idle,
  // with a 5s minimum delay to ensure the camera sweep-in finishes first.
  useEffect(() => {
    if (r3fReady && !onnxStarted) {
      const timer = setTimeout(() => {
        const startLoad = () => {
          setOnnxStarted(true)
          loadModels()
        }
        if ('requestIdleCallback' in window) {
          (window as any).requestIdleCallback(startLoad, { timeout: 2000 })
        } else {
          startLoad()
        }
      }, 800)
      return () => clearTimeout(timer)
    }
  }, [r3fReady, onnxStarted, loadModels])

  return (
    <>
      {/* Loading screen — fades out when R3F assets finish rendering */}
      <LoadingScreen ready={r3fReady} />
      <Leva hidden={!levaVisible} />

      <TranscriptOverlay />

      <div style={{ width: '100%', height: '100%', background: shell.pageBackground }}>
        <ErrorBoundary fallback={<SceneFailedFallback />}>
          <Canvas
            camera={{
              // Init position aligned with OrbitControls target [0, 0.6, 1.5]
              // for a centered medium-wide shot; init fov matches
              // CameraController's Leva default to prevent zoom-in lerp.
              position: [0, 0.6, 4.8],
              fov: 28,
              near: world.camera.near,
              far: world.camera.far,
            }}
            gl={{
              antialias: true,
              alpha: false,
              powerPreference: 'high-performance',
              // Reduce strain on mobile GPU
              pixelRatio: Math.min(window.devicePixelRatio, 2),
            }}
            shadows
            frameloop="always"
            onCreated={({ gl }) => {
              gl.setClearColor(palette.background.deep)
            }}
          >
            {/* Set scene background immediately to prevent black flash */}
            <color attach="background" args={[palette.background.deep]} />
            <Suspense fallback={null}>
              <Scene />
              <Preload all />
              {/* Fires after all Suspense children resolve (R3F assets loaded) */}
              <AssetReadySignal onReady={handleR3fReady} />
            </Suspense>
          </Canvas>
        </ErrorBoundary>
      </div>
    </>
  )
}

// ============================================================================
// APP ROOT
// ============================================================================

export default function App() {
  return (
    <VoiceProvider>
      <AppContent />
    </VoiceProvider>
  )
}
